import React from 'react';
import { useGetOne, useRedirect, useResourceContext } from 'react-admin';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useParams } from 'react-router-dom';

interface ResourceBreadcrumbsProps {
  // Optional: Override the resource name
  resource?: string;
  // Optional: Custom display names
  customNames?: {
    singular?: string;
    plural?: string;
  };
  // Optional: Parent resource info for nested resources
  parentResource?: {
    name: string;
    id: string | number;
    displayName?: string;
  };
}

const ResourceBreadcrumbs: React.FC<ResourceBreadcrumbsProps> = ({ 
  resource: resourceProp,
  customNames,
  parentResource 
}) => {
  const redirect = useRedirect();
  const resourceContext = useResourceContext();
  const { id } = useParams();
  
  // Use prop resource or context resource
  const resource = resourceProp || resourceContext;
  
  if (!resource || !id) {
    return null;
  }
  
  // Resource display name mappings
  const resourceMap: Record<string, { singular: string; plural: string }> = {
    'equipment': { singular: 'Vehicle', plural: 'Vehicles' },
    'users': { singular: 'User', plural: 'Users' },
    'drivers': { singular: 'Driver', plural: 'Drivers' },
    'driver-time-logs': { singular: 'Driver Time Log', plural: 'Driver Time Logs' },
    'equipment-maintenance': { singular: 'Maintenance', plural: 'Maintenance Records' },
    'equipment-fuel': { singular: 'Fuel Record', plural: 'Fuel Records' },
    'equipment-incidents': { singular: 'Incident', plural: 'Incidents' },
    'equipment-inspections': { singular: 'Inspection', plural: 'Inspections' },
    'equipment-assignments': { singular: 'Assignment', plural: 'Assignments' },
    'completed-forms': { singular: 'Completed Form', plural: 'Completed Forms' },
  };
  
  // Get resource names
  const resourceNames = customNames || resourceMap[resource] || {
    singular: resource.charAt(0).toUpperCase() + resource.slice(1),
    plural: resource.charAt(0).toUpperCase() + resource.slice(1) + 's'
  };
  
  // Fetch the current item data
  const { data: itemData, isLoading } = useGetOne(
    resource,
    { id },
    { enabled: !!id && !!resource }
  );
  
  // Handle navigation
  const handleNavigation = (path: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    redirect(path);
  };
  
  if (isLoading) {
    return null;
  }
  
  // Determine the item display name
  const getItemDisplayName = () => {
    if (!itemData) return resourceNames.singular;
    
    // Special handling for completed-forms
    if (resource === 'completed-forms') {
      if (itemData.form?.name) {
        return itemData.form.name;
      }
      return resourceNames.singular;
    }
    
    // Special handling for driver-time-logs
    if (resource === 'driver-time-logs' && itemData.startedAt) {
      const date = new Date(itemData.startedAt).toLocaleDateString();
      const driverName = itemData.user?.name || 'Unknown Driver';
      return `${driverName} - Time Log ${date}`;
    }
    
    // Common name fields to check
    const nameFields = ['name', 'title', 'label', 'displayName'];
    for (const field of nameFields) {
      if (itemData[field]) return itemData[field];
    }
    
    // For users, try email
    if (resource === 'users' && itemData.email) {
      return itemData.email;
    }
    
    // For drivers, try to get user name
    if (resource === 'drivers' && itemData.user?.name) {
      return itemData.user.name;
    }
    
    return resourceNames.singular;
  };

  return (
    <Box sx={{ 
      padding: '12px 16px', 
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      marginBottom: '16px',
      border: '1px solid #e0e0e0',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    }}>
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
      >
        {/* Parent resource link if provided */}
        {parentResource && (
          <Link
            underline="hover"
            color="inherit"
            href={`/${parentResource.name}/${parentResource.id}/show`}
            onClick={handleNavigation(`/${parentResource.name}/${parentResource.id}/show`)}
            sx={{ 
              fontWeight: 'medium',
              '&:hover': { color: '#1976d2' } 
            }}
          >
            {parentResource.displayName || parentResource.name}
          </Link>
        )}
        
        {/* Link to the list view */}
        <Link
          underline="hover"
          color="inherit"
          href={`/${resource}`}
          onClick={handleNavigation(`/${resource}`)}
          sx={{ 
            fontWeight: 'medium',
            '&:hover': { color: '#1976d2' } 
          }}
        >
          {resourceNames.plural}
        </Link>
        
        {/* Current item name */}
        <Typography color="text.primary" sx={{ fontWeight: 'medium' }}>
          {getItemDisplayName()}
        </Typography>
      </Breadcrumbs>
    </Box>
  );
};

export default ResourceBreadcrumbs;